"use client";

import { c as _c } from "react/compiler-runtime";
import type { VenueListItem, VenueReferenceData } from "@clutch/auth/actions/venues";
import { ArrowRight01Icon, CancelCircleIcon, Image01Icon, Search01Icon, UserGroupIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { getOpenStatus } from "@/app/v/[venueSlug]/_lib/open-status";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList, useComboboxAnchor } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { getSportConfig } from "@/config/sports";
import { cn } from "@/lib/utils";
import { VenueListEmpty } from "./venue-list-empty";
function FilterChip(t0) {
  const $ = _c(9);
  const {
    label,
    onClear
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="max-w-[160px] truncate font-medium">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `Remove ${label} filter`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <HugeiconsIcon icon={CancelCircleIcon} className="h-3.5 w-3.5" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onClear || $[4] !== t2) {
    t4 = <button type="button" aria-label={t2} onClick={onClear} className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground">{t3}</button>;
    $[3] = onClear;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4) {
    t5 = <span className="inline-flex h-7 items-center gap-1 rounded-full border border-border/70 bg-accent/40 pr-1 pl-2.5 text-xs">{t1}{t4}</span>;
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
type VenueDiscoveryFeedProps = {
  venues: VenueListItem[];
  referenceData: VenueReferenceData;
  initialDistrictSlug?: string | null;
};
export function VenueDiscoveryFeed(t0) {
  const $ = _c(86);
  const {
    venues,
    referenceData,
    initialDistrictSlug
  } = t0;
  const [search, setSearch] = useState("");
  const [districtFilter, setDistrictFilter] = useState(initialDistrictSlug ?? "");
  const [sportFilter, setSportFilter] = useState("");
  const districtAnchor = useComboboxAnchor();
  const sportAnchor = useComboboxAnchor();
  let t1;
  if ($[0] !== referenceData.districts) {
    t1 = slug => {
      const d = referenceData.districts.find(r => r.slug === slug);
      return d ? `${d.district}, ${d.state}` : slug;
    };
    $[0] = referenceData.districts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const districtName = t1;
  let t2;
  if ($[2] !== referenceData.sports) {
    t2 = slug_0 => referenceData.sports.find(s => s.slug === slug_0)?.name ?? slug_0;
    $[2] = referenceData.sports;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const sportName = t2;
  let t3;
  if ($[4] !== referenceData.districts) {
    t3 = referenceData.districts.map(_temp);
    $[4] = referenceData.districts;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const districtOptions = t3;
  let t4;
  if ($[6] !== districtOptions) {
    t4 = new Map(districtOptions.map(_temp2));
    $[6] = districtOptions;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const districtLabelsBySlug = t4;
  let t5;
  if ($[8] !== referenceData.sports) {
    t5 = referenceData.sports.map(_temp3);
    $[8] = referenceData.sports;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const sportOptions = t5;
  let t6;
  if ($[10] !== districtOptions) {
    t6 = districtOptions.map(_temp4);
    $[10] = districtOptions;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const districtLabels = t6;
  let t7;
  if ($[12] !== sportOptions) {
    t7 = sportOptions.map(_temp5);
    $[12] = sportOptions;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const sportLabels = t7;
  let t8;
  if ($[14] !== districtFilter || $[15] !== districtLabelsBySlug) {
    t8 = districtFilter ? districtLabelsBySlug.get(districtFilter) ?? districtFilter : "";
    $[14] = districtFilter;
    $[15] = districtLabelsBySlug;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const selectedDistrictLabel = t8;
  let t9;
  if ($[17] !== sportFilter || $[18] !== sportName) {
    t9 = sportFilter ? sportName(sportFilter) : "";
    $[17] = sportFilter;
    $[18] = sportName;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const selectedSportLabel = t9;
  let t10;
  if ($[20] !== districtFilter || $[21] !== districtLabelsBySlug || $[22] !== search || $[23] !== sportFilter || $[24] !== venues) {
    const q = search.trim().toLowerCase();
    t10 = venues.filter(v => {
      if (districtFilter && v.districtSlug !== districtFilter) {
        return false;
      }
      if (sportFilter && !v.sportSlugs.includes(sportFilter)) {
        return false;
      }
      if (q) {
        const districtLabel = districtLabelsBySlug.get(v.districtSlug) ?? v.districtSlug;
        const haystack = `${v.name} ${v.address} ${districtLabel}`.toLowerCase();
        if (!haystack.includes(q)) {
          return false;
        }
      }
      return true;
    });
    $[20] = districtFilter;
    $[21] = districtLabelsBySlug;
    $[22] = search;
    $[23] = sportFilter;
    $[24] = venues;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const filtered = t10;
  const hasFilters = Boolean(search) || Boolean(districtFilter) || Boolean(sportFilter);
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <HugeiconsIcon icon={Search01Icon} className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e => setSearch(e.target.value);
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== search) {
    t13 = <div className="relative flex-1">{t11}<Input placeholder="Search venues by name, address, or district" value={search} onChange={t12} className="h-10 rounded-xl border border-input bg-background pl-9 dark:border-transparent dark:bg-input/50" /></div>;
    $[28] = search;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== districtOptions) {
    t14 = v_0 => {
      const label = v_0 ?? "";
      const match = districtOptions.find(d_2 => d_2.label === label);
      setDistrictFilter(match?.slug ?? "");
    };
    $[30] = districtOptions;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ComboboxInput placeholder="District" className="h-10 rounded-xl border border-input bg-background dark:border-transparent dark:bg-input/50" showClear={true} />;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== districtAnchor) {
    t16 = <div ref={districtAnchor} className="w-full">{t15}</div>;
    $[33] = districtAnchor;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ComboboxEmpty className="py-6 text-center text-muted-foreground text-sm">No districts found.</ComboboxEmpty>;
    t18 = <ComboboxList className="max-h-56">{_temp7}</ComboboxList>;
    $[35] = t17;
    $[36] = t18;
  } else {
    t17 = $[35];
    t18 = $[36];
  }
  let t19;
  if ($[37] !== districtAnchor) {
    t19 = <ComboboxContent anchor={districtAnchor} className="rounded-2xl border-none bg-popover px-1 py-1.5 shadow-2xl">{t17}{t18}</ComboboxContent>;
    $[37] = districtAnchor;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== districtLabels || $[40] !== selectedDistrictLabel || $[41] !== t14 || $[42] !== t16 || $[43] !== t19) {
    t20 = <div className="flex-1 sm:w-56 sm:flex-initial"><Combobox value={selectedDistrictLabel} onValueChange={t14} items={districtLabels} itemToStringValue={_temp6} autoHighlight={true}>{t16}{t19}</Combobox></div>;
    $[39] = districtLabels;
    $[40] = selectedDistrictLabel;
    $[41] = t14;
    $[42] = t16;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== sportOptions) {
    t21 = v_1 => {
      const label_1 = v_1 ?? "";
      const match_0 = sportOptions.find(s_2 => s_2.label === label_1);
      setSportFilter(match_0?.slug ?? "");
    };
    $[45] = sportOptions;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <ComboboxInput placeholder="Sport" className="h-10 rounded-xl border border-input bg-background dark:border-transparent dark:bg-input/50" showClear={true} />;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== sportAnchor) {
    t23 = <div ref={sportAnchor} className="w-full">{t22}</div>;
    $[48] = sportAnchor;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  let t25;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <ComboboxEmpty className="py-6 text-center text-muted-foreground text-sm">No sports found.</ComboboxEmpty>;
    t25 = <ComboboxList className="max-h-56">{_temp9}</ComboboxList>;
    $[50] = t24;
    $[51] = t25;
  } else {
    t24 = $[50];
    t25 = $[51];
  }
  let t26;
  if ($[52] !== sportAnchor) {
    t26 = <ComboboxContent anchor={sportAnchor} className="rounded-2xl border-none bg-popover px-1 py-1.5 shadow-2xl">{t24}{t25}</ComboboxContent>;
    $[52] = sportAnchor;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== selectedSportLabel || $[55] !== sportLabels || $[56] !== t21 || $[57] !== t23 || $[58] !== t26) {
    t27 = <div className="flex-1 sm:w-44 sm:flex-initial"><Combobox value={selectedSportLabel} onValueChange={t21} items={sportLabels} itemToStringValue={_temp8} autoHighlight={true}>{t23}{t26}</Combobox></div>;
    $[54] = selectedSportLabel;
    $[55] = sportLabels;
    $[56] = t21;
    $[57] = t23;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t20 || $[61] !== t27) {
    t28 = <div className="flex gap-2">{t20}{t27}</div>;
    $[60] = t20;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t13 || $[64] !== t28) {
    t29 = <div className="flex flex-col gap-2 sm:flex-row sm:items-center">{t13}{t28}</div>;
    $[63] = t13;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  const t30 = filtered.length === venues.length ? `${venues.length} ${venues.length === 1 ? "venue" : "venues"}` : `${filtered.length} of ${venues.length} venues`;
  let t31;
  if ($[66] !== t30) {
    t31 = <span className="text-muted-foreground text-sm">{t30}</span>;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== districtFilter || $[69] !== districtName || $[70] !== hasFilters || $[71] !== search || $[72] !== sportFilter || $[73] !== sportName) {
    t32 = hasFilters && <><span aria-hidden={true} className="text-muted-foreground/40">·</span><div className="flex flex-wrap items-center gap-1.5">{search && <FilterChip label={`"${search}"`} onClear={() => setSearch("")} />}{districtFilter && <FilterChip label={districtName(districtFilter)} onClear={() => setDistrictFilter("")} />}{sportFilter && <FilterChip label={sportName(sportFilter)} onClear={() => setSportFilter("")} />}<Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground text-xs hover:text-foreground" onClick={() => {
          setSearch("");
          setDistrictFilter("");
          setSportFilter("");
        }}>Clear all</Button></div></>;
    $[68] = districtFilter;
    $[69] = districtName;
    $[70] = hasFilters;
    $[71] = search;
    $[72] = sportFilter;
    $[73] = sportName;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== t31 || $[76] !== t32) {
    t33 = <div className="flex flex-wrap items-center gap-2">{t31}{t32}</div>;
    $[75] = t31;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== filtered || $[79] !== hasFilters || $[80] !== sportName) {
    t34 = filtered.length === 0 ? <VenueListEmpty isFiltered={hasFilters} /> : <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{filtered.map(v_2 => {
        const openStatus = getOpenStatus(v_2.operatingHours);
        return <Card key={v_2.id} className="flex flex-col gap-2 overflow-hidden py-0 transition-colors hover:border-border/80"><div className="relative h-48 w-full bg-muted">{v_2.coverImageUrl ? <Image src={v_2.coverImageUrl} alt={`${v_2.name} cover`} fill={true} className="object-cover" unoptimized={true} /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><HugeiconsIcon icon={Image01Icon} className="h-8 w-8 opacity-50" /></div>}{v_2.spectatorCapacity && <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full border bg-background/90 px-2.5 py-1 font-medium text-muted-foreground text-xs shadow-sm backdrop-blur-sm"><HugeiconsIcon icon={UserGroupIcon} className="h-3.5 w-3.5" /><span>{v_2.spectatorCapacity.toLocaleString()}</span></div>}{openStatus && <div className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full border bg-background/90 px-2.5 py-1 font-medium text-xs shadow-sm backdrop-blur-sm"><span className={cn("relative flex h-2 w-2 rounded-full", openStatus.isOpen ? "bg-emerald-500" : "bg-muted-foreground")}>{openStatus.isOpen && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />}</span><span className={openStatus.isOpen ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}>{openStatus.isOpen ? "Open" : "Closed"}</span></div>}</div><CardHeader className="p-5 pb-4"><CardTitle className="line-clamp-1 font-semibold text-xl leading-tight">{v_2.name}</CardTitle>{v_2.address && <div className="mt-1.5 flex items-start gap-1.5 pr-4 text-muted-foreground text-sm"><span className="line-clamp-1 leading-snug">{v_2.address}</span></div>}</CardHeader><CardContent className="flex-grow space-y-4 p-5 pt-0"><div className="flex items-start justify-between gap-2"><div className="flex-1 space-y-2">{v_2.sportSlugs.length > 0 ? <div className="flex flex-wrap gap-2">{v_2.sportSlugs.slice(0, 2).map(s_3 => {
                    const config = getSportConfig(s_3);
                    const SportIcon = config.icon;
                    return <Badge key={s_3} variant="secondary" className="flex items-center gap-2 px-2.5 py-3.5 font-medium text-sm"><span className="shrink-0"><SportIcon className={cn("h-4.5 w-4.5", config.colorConfig.text)} /></span>{sportName(s_3)}</Badge>;
                  })}{v_2.sportSlugs.length > 2 && <Badge variant="secondary" className="flex items-center gap-2 px-2.5 py-3.5 font-medium text-muted-foreground text-sm">+{v_2.sportSlugs.length - 2} more</Badge>}</div> : <span className="text-muted-foreground text-sm">—</span>}</div></div></CardContent><CardFooter className="mt-auto p-5 pt-0"><Button asChild={true} className="w-full" variant="secondary"><Link href={{
                pathname: `/v/${v_2.slug}`
              }}>View Details<HugeiconsIcon icon={ArrowRight01Icon} className="ml-2 h-4 w-4" /></Link></Button></CardFooter></Card>;
      })}</div>;
    $[78] = filtered;
    $[79] = hasFilters;
    $[80] = sportName;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== t29 || $[83] !== t33 || $[84] !== t34) {
    t35 = <div className="space-y-4">{t29}{t33}{t34}</div>;
    $[82] = t29;
    $[83] = t33;
    $[84] = t34;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  return t35;
}
function _temp9(label_2) {
  return <ComboboxItem key={label_2} value={label_2} className="rounded-xl px-4 py-2 text-sm">{label_2}</ComboboxItem>;
}
function _temp8(item_0) {
  return item_0;
}
function _temp7(label_0) {
  return <ComboboxItem key={label_0} value={label_0} className="rounded-xl px-4 py-2 text-sm">{label_0}</ComboboxItem>;
}
function _temp6(item) {
  return item;
}
function _temp5(s_1) {
  return s_1.label;
}
function _temp4(d_1) {
  return d_1.label;
}
function _temp3(s_0) {
  return {
    slug: s_0.slug,
    label: s_0.name
  };
}
function _temp2(district) {
  return [district.slug, district.label];
}
function _temp(d_0) {
  return {
    slug: d_0.slug,
    label: `${d_0.district}, ${d_0.state}`
  };
}